'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { SmartLink } from '@/components/smart-link';
import { EmptyState } from '@/components/ui/empty-state';
import { hrefFor, loadSearchIndex, type SearchHit } from '@/lib/search';

const KIND_LABEL = { program: 'Activity', post: 'Article' } as const;
const DEBOUNCE_MS = 150;

/**
 * Unified fuzzy search over programs and blog posts. The index loads on the
 * first keystroke, so pages stay light until someone actually searches.
 */
export function SiteSearch({
  placeholder = 'Search activities and articles…',
  limit = 24,
  autoFocus = false,
}: {
  placeholder?: string;
  limit?: number;
  autoFocus?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const timer = useRef<number>(0);

  useEffect(() => {
    const trimmed = query.trim();
    window.clearTimeout(timer.current);
    if (trimmed.length < 2) return undefined;
    timer.current = window.setTimeout(async () => {
      setLoading(true);
      const engine = await loadSearchIndex();
      setReady(true);
      const results = engine.search(trimmed).slice(0, limit) as unknown as SearchHit[];
      setHits(results);
      setLoading(false);
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer.current);
  }, [query, limit]);

  const searched = query.trim().length >= 2;
  // Hits belong to the last query that ran; drop them while the box is short
  // or the debounce hasn't fired, rather than setting state inside the effect.
  const activeHits = searched ? hits : [];

  const grouped = useMemo(
    () => ({
      program: activeHits.filter((h) => h.kind === 'program'),
      post: activeHits.filter((h) => h.kind === 'post'),
    }),
    [activeHits],
  );

  return (
    <div>
      <div className="relative max-w-xl">
        <input
          type="search"
          value={query}
          autoFocus={autoFocus}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
          aria-label="Search activities and articles"
          className="w-full rounded-full border border-brand-navy/15 bg-white py-3 pr-4 pl-11 text-brand-navy placeholder:text-brand-navy/70 transition-shadow outline-none focus:border-brand-royal focus:ring-4 focus:ring-brand-royal/10"
        />
        <svg
          aria-hidden
          viewBox="0 0 24 24"
          className="absolute top-1/2 left-4 size-5 -translate-y-1/2 text-brand-navy/70"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      </div>

      {searched ? (
        <p className="mt-3 text-sm text-brand-navy/70" aria-live="polite">
          {loading && !ready
            ? 'Searching…'
            : `${activeHits.length} result${activeHits.length === 1 ? '' : 's'} for “${query.trim()}”`}
        </p>
      ) : null}

      {searched && !loading && activeHits.length === 0 && ready ? (
        <EmptyState
          tone="white"
          heading="Nothing matched that search"
          body={`We couldn't find anything for “${query.trim()}”. Try a broader term — or browse everything we run.`}
        >
          <button
            type="button"
            onClick={() => setQuery('')}
            className="rounded-full border-2 border-brand-royal px-6 py-2.5 text-sm font-bold text-brand-royal transition-colors hover:bg-brand-royal hover:text-white"
          >
            Clear search
          </button>
          <SmartLink
            href="/program-categories/all-programs"
            className="rounded-full bg-brand-yellow px-6 py-2.5 text-sm font-bold text-brand-navy transition-all hover:-translate-y-px hover:bg-brand-yellow-dark"
          >
            Browse all programs
          </SmartLink>
        </EmptyState>
      ) : null}

      {(['program', 'post'] as const).map((kind) =>
        grouped[kind].length > 0 ? (
          <section key={kind} className="mt-10">
            <h2 className="heading-md">
              {kind === 'program' ? 'Activities' : 'Articles'}
              <span className="ml-2 text-sm font-semibold text-brand-navy/70">
                {grouped[kind].length}
              </span>
            </h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {grouped[kind].map((hit) => (
                <SmartLink
                  key={hit.id}
                  href={hrefFor(hit)}
                  className="group flex gap-4 rounded-2xl border border-brand-navy/10 bg-white p-4 transition-all hover:-translate-y-0.5 hover:shadow-lg"
                >
                  {hit.image ? (
                    <Image
                      src={hit.image}
                      width={120}
                      height={90}
                      alt=""
                      className="size-16 shrink-0 rounded-xl object-cover"
                    />
                  ) : null}
                  <div className="min-w-0">
                    <p className="text-xs font-bold tracking-wide text-brand-royal uppercase">
                      {KIND_LABEL[hit.kind]}
                    </p>
                    <p className="mt-1 leading-snug font-bold text-brand-navy group-hover:text-brand-royal">
                      {hit.title}
                    </p>
                    {hit.excerpt ? (
                      <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-brand-navy/75">
                        {hit.excerpt}
                      </p>
                    ) : null}
                  </div>
                </SmartLink>
              ))}
            </div>
          </section>
        ) : null,
      )}
    </div>
  );
}
